/**
 * useCurrentSubscription Hook
 *
 * Single source of the signed-in user's subscription for the pricing hooks (VOY-610).
 * - Keyed on the user id, not the `user` object, so a token refresh does not reset it.
 * - Refetches on SIGNED_IN / TOKEN_REFRESHED / USER_UPDATED: the first lookup can leave before
 *   supabase-js attached the session and fail as `anon` (42501 since VOY-605).
 * - A failed lookup keeps the last known plan instead of downgrading the user to Free.
 * - A user change starts from that user's last known plan (or none), never the previous user's.
 *
 * @module useCurrentSubscription
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchCurrentSubscription, type SubscriptionFetchResult } from '@/lib/stripe';
import type { UserSubscription } from '@/types/subscription';

const REFETCH_EVENTS = new Set(['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED']);
// A mount starts from a plan resolved this recently and revalidates it when the window ends,
// instead of querying right away (one request per card otherwise).
const FRESH_MS = 30_000;
// Delays of the retries after a failed lookup: the auth event that would trigger a refetch may
// have fired before this instance subscribed (e.g. a page load with a restored session).
const RETRY_DELAYS_MS = [1_000, 4_000];

// Last plan resolved for a user, shared by every hook instance so a remount (e.g. the results
// list re-mounting between loading states) starts from the known plan instead of Free.
let lastKnown: { userId: string; subscription: UserSubscription | null; at: number } | null = null;

// One lookup in flight per (user, trigger), so the cards that mount together, or that all hear
// the same auth event, share a single request.
let inflight: { userId: string; key: string; promise: Promise<SubscriptionFetchResult> } | null = null;

/** Drop the shared cache (tests, and nothing else needs it: entries are keyed by user id). */
export function clearCachedSubscription(): void {
  lastKnown = null;
  inflight = null;
}

function lookup(userId: string, key: string): Promise<SubscriptionFetchResult> {
  if (inflight && inflight.userId === userId && inflight.key === key) return inflight.promise;
  const promise = fetchCurrentSubscription(userId).then((result) => {
    if (result.ok) lastKnown = { userId, subscription: result.subscription, at: Date.now() };
    return result;
  });
  inflight = { userId, key, promise };
  void promise.finally(() => {
    if (inflight?.promise === promise) inflight = null;
  });
  return promise;
}

interface State {
  userId: string | null;
  subscription: UserSubscription | null;
  /** A lookup for this user succeeded (or a cached answer exists); signed out counts as known. */
  known: boolean;
  /** Known, or every retry failed: nothing more is coming until an auth event or refetch. */
  settled: boolean;
  error: unknown;
}

function initialState(userId: string | null): State {
  const cached = userId !== null && lastKnown?.userId === userId ? lastKnown : null;
  const known = userId === null || cached !== null;
  return { userId, subscription: cached?.subscription ?? null, known, settled: known, error: null };
}

export interface CurrentSubscriptionState {
  /** Last known subscription for the user; null for none, unknown or signed out. */
  subscription: UserSubscription | null;
  /**
   * Whether `subscription` reflects a real answer. False while the first lookup is pending or
   * after it failed: a transport error is never a confirmed "no subscription", so upsells
   * must stay hidden while this is false.
   */
  isKnown: boolean;
  /** True while no answer exists and a lookup or retry is still pending. */
  isLoading: boolean;
  /** Set when the latest lookup failed; `subscription` then keeps the last known plan. */
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCurrentSubscription(userId: string | null): CurrentSubscriptionState {
  const [state, setState] = useState<State>(() => initialState(userId));
  const seq = useRef(0);

  // A user change resets synchronously, so the previous user's plan never renders for the new one.
  const view = state.userId === userId ? state : initialState(userId);

  const run = useCallback(
    async (key: string, attempt = 0): Promise<void> => {
      if (!userId) return;
      const id = ++seq.current;
      const result = await lookup(userId, key);
      if (id !== seq.current) return; // a newer lookup or a user change superseded this one
      setState((prev) => {
        const base = prev.userId === userId ? prev : initialState(userId);
        return result.ok
          ? { userId, subscription: result.subscription, known: true, settled: true, error: null }
          : { ...base, settled: base.known || attempt >= RETRY_DELAYS_MS.length, error: result.error };
      });
      if (!result.ok && attempt < RETRY_DELAYS_MS.length) {
        // A fresh key, so the retry never joins a request that left before the session did.
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
        if (id === seq.current) await run(`retry:${id}:${attempt + 1}`, attempt + 1);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (!userId) return;
    const cachedAt = lastKnown?.userId === userId ? lastKnown.at : null;
    const age = cachedAt === null ? Infinity : Date.now() - cachedAt;
    // A fresh cached plan is shown as is and revalidated when the window ends; the key is shared,
    // so every instance mounted on that plan joins one request.
    const timer = age < FRESH_MS ? setTimeout(() => void run(`expiry:${cachedAt}`), FRESH_MS - age) : undefined;
    if (timer === undefined) void run('mount');
    const lookups = seq;
    return () => {
      clearTimeout(timer);
      lookups.current++;
    };
  }, [userId, run]);

  useEffect(() => {
    if (!userId) return;
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!REFETCH_EVENTS.has(event) || session?.user?.id !== userId) return;
      // Deferred: Supabase calls made inside this callback can deadlock the auth client.
      // The access token keys the lookup so every instance shares one request per event.
      const key = `${event}:${session.access_token ?? ''}`;
      setTimeout(() => void run(key), 0);
    });
    return () => data.subscription.unsubscribe();
  }, [userId, run]);

  const refetch = useCallback(() => run(`refetch:${Date.now()}`), [run]);

  const rawError = view.error;
  const error = useMemo(
    () => (rawError ? (rawError instanceof Error ? rawError : new Error('Failed to fetch subscription')) : null),
    [rawError]
  );

  return {
    subscription: view.subscription,
    isKnown: view.known,
    isLoading: !view.settled,
    error,
    refetch,
  };
}

export default useCurrentSubscription;
